import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

/**
 * SessionGuard listens for two globally-dispatched events:
 *   • session-idle-warning  — fired ~5 minutes before idle timeout
 *   • session-expired       — fired when server returns SESSION_IDLE / SESSION_EXPIRED / SESSION_REVOKED
 *
 * It shows the "Sesi akan berakhir" banner with a "Tetap Login" button, and
 * on hard expiry it logs out + redirects to /login with a reason code so the
 * login screen can show the proper message.
 */
export default function SessionGuard() {
  const navigate = useNavigate();
  const { user, logout, heartbeat } = useAuth();
  const warningToastRef = useRef(null);
  const expiredHandledRef = useRef(false);

  useEffect(() => {
    // Only mount listeners once the user is logged in — while on /login the
    // events are irrelevant.
    if (!user) return;

    const onWarn = () => {
      if (warningToastRef.current !== null) return; // already showing
      const id = toast.warning("Sesi Anda akan berakhir karena tidak ada aktivitas.", {
        description: "Klik tombol di bawah untuk memperpanjang sesi Anda.",
        duration: Infinity,
        important: true,
        action: {
          label: "Tetap Login",
          onClick: async () => {
            const res = await heartbeat();
            if (res && !res.expired_reason) {
              toast.success("Sesi diperpanjang", { duration: 3000 });
            }
            toast.dismiss(warningToastRef.current);
            warningToastRef.current = null;
          },
        },
        onDismiss: () => { warningToastRef.current = null; },
        onAutoClose: () => { warningToastRef.current = null; },
      });
      warningToastRef.current = id;
    };

    const onExpired = async (e) => {
      if (expiredHandledRef.current) return;
      expiredHandledRef.current = true;
      const reason = e?.detail?.reason || "SESSION_IDLE";
      const query = reason === "SESSION_EXPIRED"
        ? "?reason=absolute_timeout"
        : reason === "SESSION_REVOKED"
        ? "?reason=revoked"
        : "?reason=idle_timeout";
      try { await logout(); } catch {}
      // Small delay to let toast render before redirect.
      setTimeout(() => {
        navigate(`/login${query}`, { replace: true });
        expiredHandledRef.current = false;
      }, 50);
    };

    window.addEventListener("session-idle-warning", onWarn);
    window.addEventListener("session-expired", onExpired);
    return () => {
      window.removeEventListener("session-idle-warning", onWarn);
      window.removeEventListener("session-expired", onExpired);
    };
  }, [user, logout, heartbeat, navigate]);

  return null;
}
